import {
  CarAxleTypeLabels,
  CatalogAttribute,
  CatalogTechnicalDocumentType,
  CatalogTechnicalDocumentTypeLabels,
  VehicleCatalogNodeKind,
  type VehicleCatalogNodeDetailRecord
} from '@navio/nsdk';
import {
  NavioInfoCard,
  NavioListCard,
  NoddiStaticTable,
  type NavioInfoCardItem,
  type NoddiColumnDef
} from 'noddi-ui-web';
import {
  CATALOG_ATTRIBUTE_LABEL,
  catalogSpecificationValueText,
  formatMeasure,
  getLabelMapFromEntries,
  WHEEL_FASTENER_LABEL
} from 'noddi-util';

import { CatalogBodyPhotos } from './CatalogBodyPhotos';
import { catalogFactItem } from './catalogFactItem';
import { CatalogNodeSpecifications } from './CatalogNodeSpecifications';
import { catalogPowertrainItems } from './catalogPowertrainItems';
import { catalogTrimItems } from './catalogTrimItems';
import { CatalogWheelPackages } from './CatalogWheelPackages';
import { NavioBoltPatternLabel } from './NavioBoltPatternLabel';

type CatalogNode = VehicleCatalogNodeDetailRecord;
type AxleHardware = CatalogNode['axleHardware'][number];
type TechnicalDocument = CatalogNode['technicalDocuments'][number];
/** One axle's hub geometry as text, carrying the axle it was measured on. */
type Hub = { axleType: AxleHardware['axleType']; pattern: string | null; bore: string | null };

const DOCUMENT_LABEL = getLabelMapFromEntries(CatalogTechnicalDocumentType, CatalogTechnicalDocumentTypeLabels);

const hubFact = (axle: AxleHardware): Hub => ({
  axleType: axle.axleType,
  pattern:
    axle.studHoleCount != null && axle.pitchCircleDiameterMm != null
      ? `${axle.studHoleCount}x${formatMeasure(axle.pitchCircleDiameterMm)}`
      : null,
  bore: axle.centreBoreMm != null ? `${formatMeasure(axle.centreBoreMm)} mm` : null
});

/**
 * The hub the whole node uses, or the axles that disagree. An axle with neither measurement stored
 * is not a different hub, so it never forces the table; one shape answers both questions, instead of
 * a `sharedHub` flag and a `showHubTable` flag derived from the same list.
 */
const hubGeometry = (axleHardware: AxleHardware[]): { shared: Hub | null; perAxle: Hub[] } => {
  const stated = axleHardware.map(hubFact).filter((hub) => hub.pattern != null || hub.bore != null);
  const [first] = stated;
  if (first == null) {
    return { shared: null, perAxle: [] };
  }
  const agree = stated.every((hub) => hub.pattern === first.pattern && hub.bore === first.bore);
  return agree ? { shared: first, perAxle: [] } : { shared: null, perAxle: stated };
};

const hardwareColumns: NoddiColumnDef<Hub>[] = [
  {
    header: 'Axle',
    accessorFn: (row) => CarAxleTypeLabels[row.axleType]
  },
  {
    header: CATALOG_ATTRIBUTE_LABEL[CatalogAttribute.BOLT_PATTERN],
    accessorKey: 'pattern',
    cell: ({ row }) => <NavioBoltPatternLabel value={row.original.pattern} />
  },
  {
    header: CATALOG_ATTRIBUTE_LABEL[CatalogAttribute.CENTRE_BORE],
    accessorKey: 'bore'
  }
];

const fasteningItems = (node: CatalogNode, shared: Hub | null): Array<NavioInfoCardItem | false> => [
  catalogFactItem('Wheel fastener', node.fastenerType != null ? WHEEL_FASTENER_LABEL[node.fastenerType] : null, {
    copyable: false
  }),
  ...[
    { attribute: CatalogAttribute.THREAD, fallback: node.threadSize ?? null },
    { attribute: CatalogAttribute.WHEEL_TORQUE, fallback: null },
    { attribute: CatalogAttribute.BOLT_PATTERN, fallback: shared?.pattern ?? null },
    { attribute: CatalogAttribute.CENTRE_BORE, fallback: shared?.bore ?? null }
  ].flatMap(({ attribute, fallback }) => {
    const specifications = node.specifications.filter((spec) => spec.attribute === attribute);
    const label =
      attribute === CatalogAttribute.WHEEL_TORQUE ? 'Wheel tightening torque' : CATALOG_ATTRIBUTE_LABEL[attribute];
    const values = specifications.length > 0 ? specifications.map(catalogSpecificationValueText) : [fallback];
    return values.map((value): NavioInfoCardItem | false => {
      const item = catalogFactItem(label, value);
      return item && attribute === CatalogAttribute.BOLT_PATTERN
        ? { ...item, value: <NavioBoltPatternLabel value={value} />, copyText: value ?? false }
        : item;
    });
  })
];

const hasItem = (items: Array<NavioInfoCardItem | false>) => items.some((item) => item !== false);

export const CatalogNodeFacts = ({
  node,
  showBodyPhotos = true,
  showVariantDetails = true
}: {
  node: CatalogNode;
  /** The catalog node page renders the gallery beside the family image instead. */
  showBodyPhotos?: boolean;
  /** Omit when the parent includes these facts in its details card. */
  showVariantDetails?: boolean;
}) => {
  const { shared: sharedHub, perAxle: axleHubs } = hubGeometry(node.axleHardware);
  const fastening = fasteningItems(node, sharedHub);
  const powertrain = showVariantDetails && node.powertrain != null ? catalogPowertrainItems(node.powertrain) : [];
  const trim = showVariantDetails && node.trim != null ? catalogTrimItems(node.trim) : [];

  return (
    <>
      {showBodyPhotos && <CatalogBodyPhotos node={node} />}
      {(hasItem(fastening) || axleHubs.length > 0) && (
        <NavioInfoCard title='Wheel fastening' icon='Wrench' columns={3} items={fastening}>
          {axleHubs.length > 0 && (
            <NoddiStaticTable
              columns={hardwareColumns}
              data={axleHubs}
              getRowId={(row) => row.axleType}
              wrapInCard={false}
              enableSearch={false}
              hideColumnToggle
              hideRowCount
            />
          )}
        </NavioInfoCard>
      )}
      <CatalogNodeSpecifications node={node} />
      <CatalogWheelPackages
        packages={node.wheelPackages}
        emptyText={
          node.kind === VehicleCatalogNodeKind.MODIFICATION
            ? 'Tire and rim sizes arrive from the provider the first time a car matches this modification'
            : undefined
        }
      />
      {hasItem(powertrain) && <NavioInfoCard title='Powertrain' icon='Activity' columns={3} items={powertrain} />}
      {hasItem(trim) && <NavioInfoCard title='Trim' icon='Tag' columns={3} items={trim} />}
      {node.technicalDocuments.length > 0 && (
        <NavioListCard
          title='Technical documents'
          icon='Info'
          size='small'
          items={node.technicalDocuments}
          getItemKey={(document) => document.id}
          renderItem={(document) => <DocumentRow document={document} />}
        />
      )}
    </>
  );
};

const DocumentRow = ({ document }: { document: TechnicalDocument }) => (
  <div className='flex flex-col gap-2'>
    <span className='text-sm font-medium'>{DOCUMENT_LABEL[document.type]}</span>
    <p className='text-sm whitespace-pre-wrap'>{document.text}</p>
    {document.image?.url && <img src={document.image.url} alt='' className='max-h-48 w-full object-contain' />}
    <p className='text-xs text-muted-foreground'>{document.sourceReference}</p>
  </div>
);
